import React, { useEffect, useMemo, useState } from 'react';
import Modal from '../../BaseComponents/Modal';
import { useDispatch, useSelector } from 'react-redux';
import Loader from '../../BaseComponents/Loader';
import { RootState } from '../../../store/store';
import Select, { SelectOption } from '../../BaseComponents/Select';
import Button from '../../BaseComponents/Button';
import './MemberInfoCard.scss';
import {
  assignInChargeAdminToMember,
  getInChargeAdminList,
  getMember,
} from '../../../api/members';
import { setMemberData } from '../../../store/memberSlice';
import { toast } from 'react-toastify';
import { InChargeAdminListItem } from '../../../types/apiGeneratedTypes';

type AssignInchargeModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

const AssignInchargeModal: React.FC<AssignInchargeModalProps> = ({
  isOpen,
  onClose,
}) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const [admins, setAdmins] = useState<InChargeAdminListItem[]>([]);
  const [selectedOption, setSelectedOption] = useState<SelectOption | false>(
    false
  );

  const { memberData, assignedInchargeAdminId } = useSelector(
    (state: RootState) => state.member
  );

  useEffect(() => {
    if (!isOpen) return;

    const load = async () => {
      try {
        setLoading(true);
        setError(false);
        const res = await getInChargeAdminList();
        const list = res?.admins ?? [];
        setAdmins(list);
        if (assignedInchargeAdminId) {
          const a = list.find(
            (x) => (x.user_id ?? x.id) === assignedInchargeAdminId
          );
          if (a) {
            setSelectedOption({
              key: a.user_id ?? a.id ?? '',
              value: `${a.first_name} ${a.last_name}`.trim(),
            });
          } else {
            setSelectedOption(false);
          }
        } else {
          setSelectedOption(false);
        }
      } catch (e) {
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [isOpen, assignedInchargeAdminId]);

  const adminOptions = useMemo(() => {
    return admins.map((a) => ({
      key: a.user_id ?? a.id ?? '',
      value: `${a.first_name} ${a.last_name}`.trim(),
    })).filter((o) => o.key);
  }, [admins]);

  const onAssignClick = async () => {
    if (!selectedOption || !memberData?.user_id) return;
    const key = selectedOption.key as string;
    if (!key) return;

    setSubmitting(true);
    try {
      await assignInChargeAdminToMember({
        member_id: memberData.user_id,
        admin_id: key,
      });
      const updated = await getMember(memberData.user_id);
      dispatch(setMemberData(updated));
      toast.success('In charge admin updated');
      onClose();
    } catch (err: any) {
      console.error('error assigning Incharge', err);
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.description ||
        'Failed to assign Incharge';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    onClose();
  };

  if (loading) return <Loader />;
  if (error) return <div>Error fetching Incharge list</div>;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} width={'400px'}>
      <div className='assign-doctor-modal'>
        <div className='assign-header'>Please select Incharge to assign:</div>
        <div className='select-doctor-container'>
          <Select
            name='incharge'
            placeholder='Select Incharge'
            options={adminOptions}
            selectedOption={selectedOption || { key: '', value: '' }}
            onChange={(option) => setSelectedOption(option)}
            isError={false}
          />
        </div>

        <div className='submit-btn-wrapper'>
          <Button
            text='Assign'
            onClick={onAssignClick}
            isLoading={submitting}
            isDisabled={!selectedOption || !(selectedOption as SelectOption).key}
          />
          <Button
            isLoading={submitting}
            text='Cancel'
            onClick={handleClose}
            backgroundColor={'#ffffff'}
            color={'#2D334E'}
          />
        </div>
      </div>
    </Modal>
  );
};

export default AssignInchargeModal;
